import React from 'react';

import { Box, Stack, Typography } from '@mui/material';

import { pct } from './logic';
import { reviewMoney, reviewOf, reviewPercent } from './reviewLogic';
import type { CommandDeparture, CostBasis } from './types';

export default function ReviewPanel({ departure, basis }: { departure: CommandDeparture; basis: CostBasis }) {
  const r = reviewOf(departure, basis);
  if (!r) {
    return <Typography variant="caption">Review assessment missing from this older snapshot.</Typography>;
  }
  const action = r.primary_intervention;
  return (
    <Box component="details" sx={{ p: 1.5, bgcolor: 'action.hover', minWidth: 0, overflowWrap: 'anywhere' }}>
      <Box component="summary" sx={{ cursor: 'pointer', fontWeight: 600 }}>
        RM review{action ? ` · ${action.code} ${action.name}` : ' · No action'}
      </Box>
      <Stack spacing={1} sx={{ mt: 1 }}>
        {action && (
          <Box>
            <Typography variant="subtitle2">
              {action.code} {action.name}
            </Typography>
            <Typography variant="body2">{action.reason}</Typography>
            {!!action.alternatives.length && (
              <Typography variant="caption" display="block">
                Alternatives: {action.alternatives.map((a) => `${a.code ?? ''} ${a.name}`).join('; ')}
              </Typography>
            )}
            <Typography variant="body2">{action.conditional_outcome}</Typography>
            <Typography variant="caption" display="block">
              Remaining risk: {action.remaining_risk}
            </Typography>
            <Typography variant="caption" display="block">
              {action.review_trigger}
            </Typography>
          </Box>
        )}
        <Typography variant="subtitle2">Outcomes</Typography>
        <Box role="table" aria-label="Conditional margin outcomes" sx={{ display: 'grid', gap: 0.5 }}>
          {r.outcomes.map((out) => (
            <Box
              role="row"
              key={out.label}
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '2fr repeat(4, 1fr)' },
                gap: 0.5,
                borderBottom: '1px solid',
                borderColor: 'divider',
                pb: 0.5,
              }}
            >
              <Typography role="cell" variant="caption">
                {out.label}
                {out.extra_travellers != null ? ` (${Number(out.extra_travellers.toFixed(1))} extra vs P50)` : ''}
              </Typography>
              {out.status === 'available' ? (
                <>
                  <Typography role="cell" variant="caption">
                    Margin {reviewMoney(out.margin)} · {reviewPercent(out.margin_pct)}
                  </Typography>
                  <Typography role="cell" variant="caption">
                    Change {reviewMoney(out.change_dollars)} · {out.change_pp?.toFixed(2)}pp
                  </Typography>
                  <Typography role="cell" variant="caption">
                    Dollar gap {reviewMoney(out.dollar_gap)}
                  </Typography>
                  <Typography role="cell" variant="caption">
                    Rate gap {out.rate_gap_pp?.toFixed(2)}pp
                  </Typography>
                </>
              ) : (
                <Typography role="cell" variant="caption">
                  {out.status}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
        <Typography variant="caption">
          Twin-room availability not checked. These outcomes are conditional on extra bookings.
        </Typography>
        <Typography variant="subtitle2">Discount check</Typography>
        <Typography variant="caption">
          Shows how many extra travellers the discount needs to pay for itself. It does not predict that it will get
          them.
        </Typography>
        {r.discounts.map((discount) => (
          <Box key={discount.discount}>
            <Typography variant="body2">
              {discount.discount * 100}% discount{discount.withhold ? ' · withhold' : ''}
            </Typography>
            {discount.cases.map((c) => (
              <Typography variant="caption" key={c.forecast} display="block">
                {c.forecast}: {c.status === 'available' ? `${c.extra_travellers} extra travellers` : c.status}
              </Typography>
            ))}
          </Box>
        ))}
        {r.dilution && (
          <Typography variant="body2">
            Supplier-cost dilution: booked {pct(r.dilution.booked_rate)}, predicted {pct(r.dilution.predicted_rate)}.
            Cost step +{(r.dilution.step_size * 100).toFixed(2)}% at {r.dilution.step_group_size} travellers.
          </Typography>
        )}
        {!!r.hide_candidates.length && (
          <Box>
            <Typography variant="subtitle2">Hide candidates</Typography>
            {r.hide_candidates.map((c) => (
              <Box key={c.dep_id} sx={{ my: 0.5 }}>
                <Typography variant="body2">
                  {c.tour_name} · {c.dep_name} · {c.reserved_seats} seats reserved
                </Typography>
                <Typography variant="caption">{c.checks.map((check) => `✓ ${check}`).join(' · ')}</Typography>
              </Box>
            ))}
            <Typography variant="caption">
              Candidates for a person to check. Twin availability not checked. Nothing is hidden automatically.
            </Typography>
          </Box>
        )}
      </Stack>
    </Box>
  );
}
